/**
 * Service de journalisation d'audit côté frontend
 * Enregistre les actions importantes liées à l'authentification et aux autorisations
 */

import { TokenManager } from './token-manager';
import { permissionService } from './permission-service';

// Types d'événements d'audit
enum AuditEventType {
  // Événements d'authentification
  LOGIN_SUCCESS = 'auth.login.success',
  LOGIN_FAILURE = 'auth.login.failure',
  LOGOUT = 'auth.logout',
  TOKEN_REFRESH = 'auth.token.refresh',
  TOKEN_EXPIRED = 'auth.token.expired',
  
  // Événements d'autorisation
  PERMISSION_CHECK = 'auth.permission.check',
  PERMISSION_DENIED = 'auth.permission.denied',
  ROLE_CHECK = 'auth.role.check',
  
  // Événements de tenant
  TENANT_SWITCH = 'tenant.switch',
  TENANT_ACCESS_DENIED = 'tenant.access.denied',
  
  // Événements de route
  ROUTE_ACCESS_DENIED = 'route.access.denied',
  ROUTE_NAVIGATION = 'route.navigation',
  
  // Événements d'API
  API_REQUEST = 'api.request',
  API_RESPONSE = 'api.response',
  API_ERROR = 'api.error',
  
  // Événements UI
  UI_ACTION = 'ui.action',
  UI_PERMISSION_BLOCKED = 'ui.permission.blocked'
}

// Niveaux de sévérité
enum AuditSeverity {
  DEBUG = 'debug',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Interface pour les événements d'audit
interface AuditEvent {
  timestamp: string;
  event_type: AuditEventType;
  severity: AuditSeverity;
  user_id?: string | number;
  username?: string;
  tenant_id?: string | number;
  tenant_name?: string;
  resource?: string;
  action?: string;
  status?: string;
  details?: Record<string, any>;
  source: 'frontend';
  url?: string;
  user_agent?: string;
}

// Configuration de la journalisation
interface AuditLoggerConfig {
  enabled: boolean;
  remoteLoggingUrl?: string;
  consoleLogging: boolean;
  logLevel: AuditSeverity;
  bufferSize: number;
  autoSendInterval: number; // ms
}

/**
 * Service principal de journalisation d'audit
 */
class AuditLogger {
  private config: AuditLoggerConfig = {
    enabled: true,
    consoleLogging: true,
    logLevel: AuditSeverity.INFO,
    bufferSize: 50,
    autoSendInterval: 30000 // 30 secondes
  };
  
  private eventBuffer: AuditEvent[] = [];
  private autoSendTimerId: number | null = null;
  
  constructor() {
    // Démarrer l'envoi automatique si activé
    // Uniquement côté client
    if (typeof window !== 'undefined') {
      this.startAutoSend();
      
      // Écouter les événements de déchargement de page pour envoyer les logs avant de quitter
      window.addEventListener('beforeunload', () => this.flush());
    }
  }
  
  /**
   * Configure le logger d'audit
   * @param config Configuration partielle à appliquer
   */
  configure(config: Partial<AuditLoggerConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (this.config.enabled) {
      this.startAutoSend();
    } else {
      this.stopAutoSend();
    }
  }
  
  /**
   * Démarre l'envoi automatique des événements d'audit
   */
  private startAutoSend(): void {
    // Ne pas exécuter côté serveur
    if (typeof window === 'undefined') {
      return;
    }
    
    if (this.autoSendTimerId !== null) {
      clearInterval(this.autoSendTimerId);
    }
    
    if (this.config.enabled && this.config.autoSendInterval > 0) {
      this.autoSendTimerId = window.setInterval(() => {
        this.sendEvents();
      }, this.config.autoSendInterval);
    }
  }
  
  /**
   * Arrête l'envoi automatique des événements d'audit
   */
  private stopAutoSend(): void {
    // Ne pas exécuter côté serveur
    if (typeof window === 'undefined') {
      return;
    }
    
    if (this.autoSendTimerId !== null) {
      clearInterval(this.autoSendTimerId);
      this.autoSendTimerId = null;
    }
  }
  
  /**
   * Journalise un événement d'audit
   * @param eventType Type d'événement
   * @param severity Niveau de sévérité
   * @param details Détails supplémentaires de l'événement
   */
  log(eventType: AuditEventType, severity: AuditSeverity = AuditSeverity.INFO, details: Record<string, any> = {}): void {
    if (!this.config.enabled || this.getSeverityLevel(severity) < this.getSeverityLevel(this.config.logLevel)) {
      return;
    }
    
    // Récupérer les informations utilisateur depuis le token
    const userProfile = TokenManager.getUserProfile();
    
    // Créer l'événement d'audit
    const auditEvent: AuditEvent = {
      timestamp: new Date().toISOString(),
      event_type: eventType,
      severity,
      details,
      source: 'frontend',
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    };
    
    // Ajouter les informations utilisateur si disponibles
    if (userProfile) {
      auditEvent.user_id = userProfile.id;
      auditEvent.username = userProfile.username;
      
      // Ajouter les informations de tenant si disponibles
      if (userProfile.current_tenant) {
        auditEvent.tenant_id = userProfile.current_tenant.id;
        auditEvent.tenant_name = userProfile.current_tenant.name;
      }
    }
    
    // Journaliser dans la console si activé
    if (this.config.consoleLogging) {
      this.logToConsole(auditEvent);
    }
    
    // Ajouter l'événement au buffer
    this.eventBuffer.push(auditEvent);
    
    // Envoyer les événements si le buffer est plein
    if (this.eventBuffer.length >= this.config.bufferSize) {
      this.sendEvents();
    }
  }
  
  /**
   * Journalise un événement d'audit au niveau DEBUG
   * @param eventType Type d'événement
   * @param details Détails supplémentaires de l'événement
   */
  debug(eventType: AuditEventType, details: Record<string, any> = {}): void {
    this.log(eventType, AuditSeverity.DEBUG, details);
  }
  
  /**
   * Journalise un événement d'audit au niveau INFO
   * @param eventType Type d'événement
   * @param details Détails supplémentaires de l'événement
   */
  info(eventType: AuditEventType, details: Record<string, any> = {}): void {
    this.log(eventType, AuditSeverity.INFO, details);
  }
  
  /**
   * Journalise un événement d'audit au niveau WARNING
   * @param eventType Type d'événement
   * @param details Détails supplémentaires de l'événement
   */
  warn(eventType: AuditEventType, details: Record<string, any> = {}): void {
    this.log(eventType, AuditSeverity.WARNING, details);
  }
  
  /**
   * Journalise un événement d'audit au niveau ERROR
   * @param eventType Type d'événement
   * @param details Détails supplémentaires de l'événement
   */
  error(eventType: AuditEventType, details: Record<string, any> = {}): void {
    this.log(eventType, AuditSeverity.ERROR, details);
  }
  
  /**
   * Journalise un événement d'audit au niveau CRITICAL
   * @param eventType Type d'événement
   * @param details Détails supplémentaires de l'événement
   */
  critical(eventType: AuditEventType, details: Record<string, any> = {}): void {
    this.log(eventType, AuditSeverity.CRITICAL, details);
  }
  
  /**
   * Journalise un événement de vérification de permission
   * @param permission Permission vérifiée
   * @param granted Résultat de la vérification
   * @param details Détails supplémentaires
   */
  logPermissionCheck(permission: string, granted: boolean, details: Record<string, any> = {}): void {
    if (granted) {
      this.debug(AuditEventType.PERMISSION_CHECK, {
        permission,
        granted,
        ...details
      });
    } else {
      this.warn(AuditEventType.PERMISSION_DENIED, {
        permission,
        granted,
        ...details
      });
    }
  }
  
  /**
   * Journalise un événement de changement de tenant
   * @param previousTenantId ID du tenant précédent
   * @param newTenantId ID du nouveau tenant
   * @param details Détails supplémentaires
   */
  logTenantSwitch(previousTenantId: string | number | null, newTenantId: string | number, details: Record<string, any> = {}): void {
    this.info(AuditEventType.TENANT_SWITCH, {
      previous_tenant_id: previousTenantId,
      new_tenant_id: newTenantId,
      ...details
    });
  }
  
  /**
   * Journalise un événement de navigation
   * @param fromRoute Route source
   * @param toRoute Route destination
   * @param details Détails supplémentaires
   */
  logNavigation(fromRoute: string, toRoute: string, details: Record<string, any> = {}): void {
    this.debug(AuditEventType.ROUTE_NAVIGATION, {
      from_route: fromRoute,
      to_route: toRoute,
      ...details
    });
  }
  
  /**
   * Journalise un événement d'action UI
   * @param component Composant concerné
   * @param action Action effectuée
   * @param details Détails supplémentaires
   */
  logUIAction(component: string, action: string, details: Record<string, any> = {}): void {
    this.debug(AuditEventType.UI_ACTION, {
      component,
      action,
      ...details
    });
  }
  
  /**
   * Envoie les événements d'audit au serveur
   * @returns Promesse résolue lorsque les événements sont envoyés
   */
  private async sendEvents(): Promise<void> {
    if (!this.config.enabled || this.eventBuffer.length === 0 || !this.config.remoteLoggingUrl) {
      return;
    }
    
    try {
      // Copier le buffer actuel et le vider
      const eventsToSend = [...this.eventBuffer];
      this.eventBuffer = [];
      
      // Envoyer les événements au serveur
      const response = await fetch(this.config.remoteLoggingUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ events: eventsToSend }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        // En cas d'échec, remettre les événements dans le buffer
        this.eventBuffer = [...eventsToSend, ...this.eventBuffer];
        console.error('Échec de l\'envoi des événements d\'audit:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi des événements d\'audit:', error);
      // En cas d'erreur, remettre les événements dans le buffer
      this.eventBuffer = [...this.eventBuffer];
    }
  }
  
  /**
   * Envoie immédiatement tous les événements en attente
   */
  async flush(): Promise<void> {
    await this.sendEvents();
  }
  
  /**
   * Affiche un événement d'audit dans la console
   * @param event Événement à afficher
   */
  private logToConsole(event: AuditEvent): void {
    const timestamp = new Date(event.timestamp).toLocaleTimeString();
    const prefix = `[${timestamp}] [${event.severity.toUpperCase()}] [${event.event_type}]`;
    
    switch (event.severity) {
      case AuditSeverity.DEBUG:
        console.debug(prefix, event);
        break;
      case AuditSeverity.INFO:
        console.info(prefix, event);
        break;
      case AuditSeverity.WARNING:
        console.warn(prefix, event);
        break;
      case AuditSeverity.ERROR:
      case AuditSeverity.CRITICAL:
        console.error(prefix, event);
        break;
    }
  }
  
  /**
   * Journalise un accès API
   * @param details Informations sur l'accès API
   */
  logApiAccess(details: {
    url: string;
    method: string;
    authenticated: boolean;
    statusCode?: number;
    responseTime?: number;
  }): void {
    this.debug(AuditEventType.API_REQUEST, {
      url: details.url,
      method: details.method,
      authenticated: details.authenticated,
      status_code: details.statusCode,
      response_time_ms: details.responseTime
    });
  }

  /**
   * Journalise une erreur d'authentification
   * @param details Informations sur l'erreur d'authentification
   */
  logAuthError(details: {
    type: string;
    message: string;
    statusCode?: number;
    resourceUrl?: string;
    attemptedAction?: string;
  }): void {
    this.error(AuditEventType.LOGIN_FAILURE, {
      error_type: details.type,
      message: details.message,
      status_code: details.statusCode,
      resource_url: details.resourceUrl,
      attempted_action: details.attemptedAction,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Convertit un niveau de sévérité en valeur numérique pour comparaison
   * @param severity Niveau de sévérité
   * @returns Valeur numérique représentant le niveau
   */
  private getSeverityLevel(severity: AuditSeverity): number {
    switch (severity) {
      case AuditSeverity.DEBUG:
        return 1;
      case AuditSeverity.INFO:
        return 2;
      case AuditSeverity.WARNING:
        return 3;
      case AuditSeverity.ERROR:
        return 4;
      case AuditSeverity.CRITICAL:
        return 5;
      default:
        return 0;
    }
  }
}

// Exporter une instance unique pour toute l'application
// Création conditionnelle pour éviter les problèmes de SSR
let auditLogger: AuditLogger;

// Vérifier que nous sommes côté client pour initialiser l'instance
if (typeof window !== 'undefined') {
  auditLogger = new AuditLogger();
} else {
  // Version stub pour le côté serveur
  auditLogger = new AuditLogger();
  // Désactiver la journalisation côté serveur
  auditLogger.configure({ enabled: false });
}

// Exporter le logger, la classe et les enums
export {
  auditLogger,
  AuditLogger,
  AuditEventType,
  AuditSeverity
};

// Exporter les types avec la syntaxe 'export type'
export type { AuditEvent };
